/**
 * CORE-1479 — наградные реакции: три картинки комплекта, которые ставит только
 * тот, кто заполнил профиль в первый месяц (ладонь с сердцем, инь-ян, сердечко
 * с объятиями). Видят их все — так награда и работает маркером.
 *
 * Решает сервер (и в чате, и под публикациями, и в эфире он отказывает кодом
 * `reward_reaction_locked`). Здесь — только то, что показать человеку, чтобы
 * отказа он не встретил:
 *
 *   open   — реакция как реакция;
 *   locked — видна с замком, нажатие объясняет, до какого числа она откроется;
 *   hidden — открыть её уже нельзя (срок вышел, мастер, гость): в выборе её
 *            нет, а под чужими сообщениями она по-прежнему видна.
 *
 * Состояние живёт в пользователе (`/me`); ответ на сохранение профиля
 * приносит свежее через `rewardReactionsBus`, его подхватывает AuthContext,
 * с какого бы экрана ни сохраняли.
 */
import { Alert, Platform } from 'react-native';
import i18n from '../i18n';
import { formatDayMonthUtc, getDateNames } from '../i18n/dateNames';
import { showFlashNotice } from './flashNotice';
import type { RewardReactionsState } from '../types/auth';

export { rewardAccessOf, type RewardAccess } from './rewardReactionsAccess';

/**
 * Что сказать человеку, нажавшему на закрытую наградную реакцию.
 *
 * День срока — по UTC, как его называет сервер и Агапелла (58385: «дата в
 * подсказке — та же, что на сервере»). По местному времени к востоку от UTC
 * вечерний срок переезжал на завтра: `2026-11-07T18:27Z` в Бишкеке звучал «до 8
 * ноября», а Агапелла говорила «до 7 ноября» (возврат QA 28.10).
 */
export function rewardLockedMessage(state: RewardReactionsState | null | undefined): string {
  const until = state?.availableUntilUtc ? new Date(state.availableUntilUtc) : null;
  if (until && !Number.isNaN(until.getTime()) && until.getTime() > Date.now()) {
    return i18n.t('common:rewardReactions.locked', { date: formatDayMonthUtc(until, getDateNames()) });
  }
  return i18n.t('common:rewardReactions.rewardOnly');
}

function notify(message: string): void {
  // Хост тостов живёт только на вебе (FlashNoticeHost); на нативе — Alert.
  if (Platform.OS === 'web') showFlashNotice(message);
  else Alert.alert('', message);
}

export function notifyRewardLocked(state: RewardReactionsState | null | undefined): void {
  notify(rewardLockedMessage(state));
}

export function notifyRewardUnlocked(): void {
  notify(i18n.t('common:rewardReactions.unlocked'));
}
